import { useEffect, useState, useMemo, useCallback } from 'react';

interface Project {
  key: string;
  display_name: string;
  github_repo: string;
  color: string | null;
  local_only: number;
  default_target: 'sitm' | 'sitm-local';
}

interface Push {
  id: number;
  project_key: string;
  state: 'queued' | 'proposed' | 'push-ready' | 'pushed';
  target: 'sitm' | 'sitm-local';
  source_path: string;
  filename: string;
  target_subdir: string | null;
  commit_message: string | null;
  push_sha: string | null;
  contract_week: string | null;
  pushed_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  reason: string | null;
  notes: string | null;
}

export function PushPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<string>(() => {
    try { return localStorage.getItem('dock::active-project') || 'sonographer'; }
    catch { return 'sonographer'; }
  });
  const [pushes, setPushes] = useState<Push[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState('');
  // Week filter on the Pushed history. 'auto' = latest week present in data;
  // 'all' = no filter; otherwise a specific week string like "week 6".
  const [weekFilter, setWeekFilter] = useState<string>(() => {
    try { return localStorage.getItem('dock::week-filter') || 'auto'; }
    catch { return 'auto'; }
  });

  const fetchProjects = useCallback(async () => {
    const resp = await fetch('/api/dock/projects');
    if (!resp.ok) throw new Error('HTTP ' + resp.status);
    const json = await resp.json();
    setProjects(json.projects || []);
  }, []);

  const fetchPushes = useCallback(async (project: string) => {
    const resp = await fetch('/api/dock/pushes?project=' + encodeURIComponent(project));
    if (!resp.ok) throw new Error('HTTP ' + resp.status);
    const json = await resp.json();
    setPushes(json.pushes || []);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      await fetchProjects();
      await fetchPushes(activeProject);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [activeProject, fetchProjects, fetchPushes]);

  useEffect(() => { void refresh(); }, [refresh]);

  const switchProject = (key: string) => {
    setActiveProject(key);
    try { localStorage.setItem('dock::active-project', key); } catch {}
  };

  const byState = useMemo(() => {
    const grouped: Record<Push['state'], Push[]> = { 'push-ready': [], 'queued': [], 'proposed': [], 'pushed': [] };
    for (const p of pushes) grouped[p.state]?.push(p);
    return grouped;
  }, [pushes]);

  // Discover available weeks from the pushed history. Sort by the trailing
  // integer descending so "week 10" beats "week 9".
  const availableWeeks = useMemo(() => {
    const set = new Set<string>();
    for (const p of byState['pushed']) if (p.contract_week) set.add(p.contract_week);
    return Array.from(set).sort((a, b) => {
      const na = parseInt(a.replace(/\D+/g, ''), 10);
      const nb = parseInt(b.replace(/\D+/g, ''), 10);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return nb - na;
      return b.localeCompare(a);
    });
  }, [byState]);

  const resolvedWeek = weekFilter === 'auto' ? (availableWeeks[0] || 'all') : weekFilter;

  const pushedFiltered = useMemo(() => {
    if (resolvedWeek === 'all') return byState['pushed'];
    return byState['pushed'].filter(p => p.contract_week === resolvedWeek);
  }, [byState, resolvedWeek]);

  const setWeek = (w: string) => {
    setWeekFilter(w);
    try { localStorage.setItem('dock::week-filter', w); } catch {}
  };

  const addPaste = async () => {
    const lines = pasteText.split('\n').map(s => s.trim()).filter(Boolean);
    if (lines.length === 0) return;
    const project = projects.find(p => p.key === activeProject);
    const target = project?.default_target || 'sitm';
    for (const line of lines) {
      const path = line.replace(/^file:\/\//, '').replace(/%20/g, ' ');
      await fetch('/api/dock/pushes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_key: activeProject,
          state: 'queued',
          target,
          source_path: path,
          created_by: 'kjr',
        }),
      });
    }
    setPasteText('');
    void fetchPushes(activeProject);
  };

  const updatePush = async (id: number, body: Partial<Push>) => {
    await fetch('/api/dock/pushes/' + id, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    void fetchPushes(activeProject);
  };

  const deletePush = async (id: number) => {
    await fetch('/api/dock/pushes/' + id, { method: 'DELETE' });
    void fetchPushes(activeProject);
  };

  const activeProj = projects.find(p => p.key === activeProject);

  return (
    <div className="dock-shell">
      <header className="dock-header">
        <h1>dock</h1>
        <nav className="dock-tabs">
          {projects.map(p => (
            <button
              key={p.key}
              type="button"
              className={'dock-tab' + (p.key === activeProject ? ' dock-tab-active' : '')}
              style={p.color ? { borderColor: p.color } : undefined}
              onClick={() => switchProject(p.key)}
            >
              {p.display_name}
              <span className="dock-tab-repo">{p.github_repo}</span>
            </button>
          ))}
        </nav>
        <div className="dock-actions">
          {loading ? <span className="dock-loading">loading</span> : null}
          <button type="button" onClick={() => void refresh()} className="dock-refresh">refresh</button>
        </div>
      </header>

      {error ? <div className="dock-error">error: {error}</div> : null}

      <section className="dock-section dock-section-pushready">
        <h2>Push <span className="dock-count">{byState['push-ready'].length}</span></h2>
        {byState['push-ready'].length === 0 ? (
          <div className="dock-empty">no commands ready. claude writes these via <code>dock push</code></div>
        ) : (
          <ul className="dock-pushready">
            {byState['push-ready'].map(p => (
              <PushReadyRow key={p.id} push={p} onDelete={() => void deletePush(p.id)} />
            ))}
          </ul>
        )}
      </section>

      <div className="dock-split">
        <section className="dock-section dock-section-queue">
          <h2>Queued <span className="dock-count">{byState['queued'].length}</span></h2>
          {byState['queued'].length === 0 ? (
            <div className="dock-empty dock-empty-mini">paste filepaths below to queue them</div>
          ) : (
            <ul className="dock-queue-list">
              {byState['queued'].map(p => (
                <li key={p.id} className="dock-queue-row">
                  <div className="dock-queue-name">{p.filename}</div>
                  <div className="dock-queue-path">{p.source_path}</div>
                  <div className="dock-queue-actions">
                    <button type="button" onClick={() => void updatePush(p.id, { state: 'proposed' })}>→ propose</button>
                    <button type="button" onClick={() => void deletePush(p.id)} className="dock-btn-danger">delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="dock-paste">
            <textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder={'paste filepaths here, one per line\nthey get added to the queue under ' + (activeProj?.display_name || activeProject)}
              rows={5}
            />
            <button type="button" onClick={() => void addPaste()} disabled={pasteText.trim().length === 0}>add to queue</button>
          </div>
        </section>

        <section className="dock-section dock-section-proposed">
          <h2>Proposed <span className="dock-count">{byState['proposed'].length}</span></h2>
          {byState['proposed'].length === 0 ? (
            <div className="dock-empty dock-empty-mini">no proposals. claudes propose via <code>dock propose</code></div>
          ) : (
            <ul className="dock-proposed-list">
              {byState['proposed'].map(p => (
                <li key={p.id} className="dock-proposed-row">
                  <div className="dock-proposed-name">{p.filename}</div>
                  <div className="dock-proposed-path">{p.source_path}</div>
                  {p.reason ? <div className="dock-proposed-reason">{p.reason}</div> : null}
                  <div className="dock-proposed-meta">
                    <span className="dock-by">{p.created_by || 'unknown'}</span>
                  </div>
                  <div className="dock-proposed-actions">
                    <button type="button" onClick={() => void updatePush(p.id, { state: 'queued' })}>→ queue</button>
                    <button type="button" onClick={() => void deletePush(p.id)} className="dock-btn-danger">reject</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="dock-section dock-section-pushed">
        <h2>
          Pushed
          <span className="dock-count">{pushedFiltered.length}{resolvedWeek !== 'all' && byState['pushed'].length !== pushedFiltered.length ? ' / ' + byState['pushed'].length : ''}</span>
          <span className="dock-week-filter">
            <select
              value={weekFilter === 'auto' ? resolvedWeek : weekFilter}
              onChange={e => setWeek(e.target.value)}
              aria-label="Filter pushed by week"
            >
              {availableWeeks.map(w => <option key={w} value={w}>{w}</option>)}
              <option value="all">all weeks</option>
            </select>
          </span>
        </h2>
        {pushedFiltered.length === 0 ? (
          <div className="dock-empty">{byState['pushed'].length === 0 ? 'no recorded pushes yet. backfill from git: ' : 'no pushed entries for ' + resolvedWeek + '. switch the filter above.'}{byState['pushed'].length === 0 ? <code>dock-backfill.sh</code> : null}</div>
        ) : (
          <div className="dock-table-wrap">
            <table className="dock-table">
              <thead>
                <tr>
                  <th>filename</th>
                  <th>week</th>
                  <th>pushed</th>
                  <th>subdir</th>
                  <th>sha</th>
                  <th>source path</th>
                  <th>commit message</th>
                </tr>
              </thead>
              <tbody>
                {pushedFiltered.map(p => (
                  <tr key={p.id}>
                    <td className="dock-td-filename">{p.filename}</td>
                    <td>{p.contract_week || ''}</td>
                    <td className="dock-td-time">{formatPushedAt(p.pushed_at)}</td>
                    <td className="dock-td-subdir">{p.target_subdir || ''}</td>
                    <td className="dock-td-sha">{p.push_sha ? p.push_sha.slice(0, 7) : ''}</td>
                    <td className="dock-td-source">{p.source_path}</td>
                    <td className="dock-td-msg">{p.commit_message || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function PushReadyRow({ push, onDelete }: { push: Push; onDelete: () => void }) {
  const [copied, setCopied] = useState(false);
  const command = buildWorkPushCommand(push);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <li className="dock-pushready-row">
      <div className="dock-pushready-head">
        <span className="dock-pushready-name">{push.filename}</span>
        <span className="dock-pushready-target">→ {push.target}/{push.target_subdir || ''}</span>
        <div className="dock-pushready-actions">
          <button type="button" onClick={() => void copy()}>{copied ? 'copied' : 'copy'}</button>
          <button type="button" onClick={onDelete} className="dock-btn-danger">drop</button>
        </div>
      </div>
      <pre className="dock-pushready-cmd">{command}</pre>
      {push.commit_message ? <div className="dock-pushready-msg">{push.commit_message}</div> : null}
    </li>
  );
}

function buildWorkPushCommand(p: Push): string {
  const verb = p.target === 'sitm-local' ? 'push-local' : 'push';
  const subdir = p.target_subdir || '.';
  const msg = p.commit_message ? ' -m "' + p.commit_message.replace(/"/g, '\\"') + '"' : '';
  return 'work project use ' + p.project_key + ' && work ' + verb + ' ' + subdir + ' ' + p.source_path + msg;
}

function formatPushedAt(ts: string | null): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ts; }
}
